import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { FaCalculator, FaSave, FaFileExcel } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { validateTCNumber } from '../utils/validators';

const ReportGeneratorPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [tcError, setTcError] = useState('');

    const [formData, setFormData] = useState({
        title: '',
        customerData: {
            name: '',
            phone: '',
            email: '',
            tcNo: '',
            address: ''
        },
        vehicleData: {
            make: '',
            model: '',
            price: 0,
            plate: '',
            motorNo: '',
            saseNo: '',
            tramer: 'YOK',
            tramerAmount: 0,
            tramerDescription: ''
        },
        financialData: {
            downPayment: 0,
            interestRate: 7.0, // Default %7 aylık
            term: 1
        },
        notes: ''
    });

    // Formatlanmış fiyat ve peşinat için ayrı state'ler
    const [formattedPrice, setFormattedPrice] = useState('0');
    const [formattedDownPayment, setFormattedDownPayment] = useState('0');
    const [formattedTramerAmount, setFormattedTramerAmount] = useState('0');

    const [calculations, setCalculations] = useState(null);

    // Sayıyı binlik ayırıcı ile formatlama
    const formatNumber = (num) => {
        if (!num || num === 0) return '0';
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    // String'den binlik ayırıcıları kaldır ve sayıya çevir
    const parseFormattedNumber = (str) => {
        if (!str || str === '') return 0;
        const cleaned = str.replace(/\./g, '');
        return parseInt(cleaned, 10) || 0;
    };

    // Formatlanmış input değişikliklerini handle et
    const handleFormattedNumberChange = (field, value) => {
        // Sadece rakam ve nokta kabul et
        const cleanValue = value.replace(/[^\d]/g, '');

        if (cleanValue === '') {
            if (field === 'price') {
                setFormattedPrice('0');
                setFormData(prev => ({
                    ...prev,
                    vehicleData: { ...prev.vehicleData, price: 0 }
                }));
            } else if (field === 'downPayment') {
                setFormattedDownPayment('0');
                setFormData(prev => ({
                    ...prev,
                    financialData: { ...prev.financialData, downPayment: 0 }
                }));
            }
            return;
        }

        const numericValue = parseInt(cleanValue, 10);
        const formatted = formatNumber(numericValue);

        if (field === 'price') {
            setFormattedPrice(formatted);
            setFormData(prev => ({
                ...prev,
                vehicleData: { ...prev.vehicleData, price: numericValue }
            }));
        } else if (field === 'downPayment') {
            setFormattedDownPayment(formatted);
            setFormData(prev => ({
                ...prev,
                financialData: { ...prev.financialData, downPayment: numericValue }
            }));
        } else if (field === 'tramerAmount') {
            setFormattedTramerAmount(formatted);
            setFormData(prev => ({
                ...prev,
                vehicleData: { ...prev.vehicleData, tramerAmount: numericValue }
            }));
        }
    };

    const handleChange = (section, field, value) => {
        // Text alanları için büyük harf dönüşümü
        const textFields = ['name', 'make', 'model', 'plate', 'address', 'saseNo', 'motorNo'];
        if (textFields.includes(field)) {
            value = value.toUpperCase();
        }

        // TC Kimlik için özel kontroller (ApplicationPage.js'den kopyalandı)
        if (field === 'tcNo') {
            // Sadece sayı girişine izin ver
            if (value && !/^[0-9]+$/.test(value)) {
                return;
            }

            // TC Kimlik doğrulama hatasını sıfırla
            setTcError('');

            // TC Kimlik uzunluğu 11 haneden fazla olmamalı
            if (value.length > 11) {
                return;
            }

            // 11 haneye ulaştığında doğrulama yap
            if (value.length === 11) {
                if (!validateTCNumber(value)) {
                    setTcError('Geçersiz T.C. Kimlik Numarası');
                }
            }
        }

        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [parent]: {
                        ...prev[section][parent],
                        [child]: value
                    }
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));
        }
    };

    // PMT fonksiyonu - Excel'deki PMT fonksiyonu ile aynı mantık
    const calculatePMT = (rate, nper, pv) => {
        if (rate === 0) return pv / nper;

        const pvif = Math.pow(1 + rate, nper);
        const pmt = (rate * pv * pvif) / (pvif - 1);
        return pmt;
    };

    const calculateFinancials = () => {
        const { vehicleData, financialData } = formData;
        const loanAmount = vehicleData.price - financialData.downPayment;

        if (loanAmount <= 0 || financialData.term <= 0) {
            setCalculations(null);
            return;
        }

        const monthlyInterestRate = financialData.interestRate / 100; // Aylık faiz oranı

        // PMT hesaplaması
        const monthlyPayment = calculatePMT(monthlyInterestRate, financialData.term, loanAmount);
        const totalPayment = monthlyPayment * financialData.term;
        const totalInterest = totalPayment - loanAmount;

        setCalculations({
            loanAmount: Math.round(loanAmount),
            monthlyPayment: Math.round(monthlyPayment),
            totalPayment: Math.round(totalPayment),
            totalInterest: Math.round(totalInterest)
        });
    };

    useEffect(() => {
        if (formData.vehicleData.price > 0 && formData.financialData.downPayment >= 0 && formData.financialData.term > 0) {
            calculateFinancials();
        } else {
            setCalculations(null);
        }
    }, [formData.vehicleData.price, formData.financialData.downPayment, formData.financialData.term, formData.financialData.interestRate]);

    // Component mount olduğunda formatlanmış değerleri set et
    useEffect(() => {
        setFormattedPrice(formatNumber(formData.vehicleData.price));
        setFormattedDownPayment(formatNumber(formData.financialData.downPayment));
        setFormattedTramerAmount(formatNumber(formData.vehicleData.tramerAmount));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // TC Kimlik doğrulama kontrolü
        if (formData.customerData.tcNo && !validateTCNumber(formData.customerData.tcNo)) {
            setTcError('Geçersiz T.C. Kimlik Numarası');
            setError('Lütfen geçerli bir T.C. Kimlik Numarası giriniz.');
            setLoading(false);
            return;
        }

        try {
            console.log("Token var mı:", !!localStorage.getItem('token'));

            // Otomatik başlık oluştur
            const title = `${formData.customerData.name} - ${formData.vehicleData.make} ${formData.vehicleData.model} Finansman Raporu`;

            // word-template-processor formatına uygun veri oluştur
            const templateData = {
                musteriAdi: formData.customerData.name,
                tcKimlik: formData.customerData.tcNo,
                adres: formData.customerData.address,
                telefon: formData.customerData.phone,
                plaka: formData.vehicleData.plate,
                marka: formData.vehicleData.make,
                model: formData.vehicleData.model,
                motorNo: formData.vehicleData.motorNo,
                saseNo: formData.vehicleData.saseNo,
                tramer: formData.vehicleData.tramer,
                tramerAmount: formData.vehicleData.tramerAmount,
                tramerDescription: formData.vehicleData.tramerDescription,
                sozlesmeTarihi: new Date().toISOString().split('T')[0],
                aracPesinFiyati: formData.vehicleData.price.toString(),
                pesinOdenen: formData.financialData.downPayment.toString(),
                vadeMiktari: formData.financialData.term.toString(),
                faizOraniAylik: formData.financialData.interestRate.toString(),
                // Hesaplanan değerler
                aylikOdeme: calculations?.monthlyPayment.toString()
            };

            // axios yerine api kullanın
            const response = await api.post('/reports', {
                ...formData,
                title,
                templateData // Word şablonu için gerekli ek veri
            });

            console.log("API yanıtı:", response);

            if (response.data.success) {
                setSuccess('Rapor başarıyla oluşturuldu!');
                setTimeout(() => {
                    navigate('/reports');
                }, 1500);
            }
        } catch (error) {
            console.error("API hatası:", error);

            let errorMessage = 'Rapor oluşturulurken hata oluştu';

            if (error.response) {
                console.log("Durum kodu:", error.response.status);
                console.log("Hata verisi:", error.response.data);
                errorMessage = error.response.data.message || errorMessage;
            } else if (error.request) {
                console.log("İstek yapıldı ama yanıt alınamadı");
                errorMessage = 'Sunucuya bağlanılamadı. Lütfen internet bağlantınızı kontrol edin.';
            } else {
                console.log("Hata mesajı:", error.message);
                errorMessage = error.message;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-5">
            <Row className="mb-4">
                <Col>
                    <h1 className="text-center">Finansman Raporu Oluştur</h1>
                    <p className="text-center text-muted">
                        Müşteri ve araç bilgilerini girerek detaylı finansman raporu oluşturun
                    </p>
                </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Form onSubmit={handleSubmit}>
                <Row>
                    {/* Müşteri Bilgileri */}
                    <Col md={4}>
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">Müşteri Bilgileri</h5>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Ad Soyad *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.customerData.name}
                                        onChange={(e) => handleChange('customerData', 'name', e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Telefon *</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        value={formData.customerData.phone}
                                        onChange={(e) => handleChange('customerData', 'phone', e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={formData.customerData.email}
                                        onChange={(e) => handleChange('customerData', 'email', e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>TC No *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.customerData.tcNo}
                                        onChange={(e) => handleChange('customerData', 'tcNo', e.target.value)}
                                        maxLength={11}
                                        className={tcError ? "is-invalid" : ""}
                                        required
                                    />
                                    {tcError && (
                                        <div className="invalid-feedback">
                                            {tcError}
                                        </div>
                                    )}
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Adres</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.customerData.address}
                                        onChange={(e) => handleChange('customerData', 'address', e.target.value)}
                                    />
                                </Form.Group>

                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Araç Bilgileri */}
                    <Col md={4}>
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">Araç Bilgileri</h5>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Marka *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.vehicleData.make}
                                        onChange={(e) => handleChange('vehicleData', 'make', e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Model *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.vehicleData.model}
                                        onChange={(e) => handleChange('vehicleData', 'model', e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Fiyat (TL) *</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">₺</span>
                                        <Form.Control
                                            type="text"
                                            value={formattedPrice}
                                            onChange={(e) => handleFormattedNumberChange('price', e.target.value)}
                                            placeholder="0"
                                            required
                                        />
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Plaka *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.vehicleData.plate}
                                        onChange={(e) => handleChange('vehicleData', 'plate', e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Şase No</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.vehicleData.saseNo}
                                        onChange={(e) => handleChange('vehicleData', 'saseNo', e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Motor No</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.vehicleData.motorNo}
                                        onChange={(e) => handleChange('vehicleData', 'motorNo', e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Tramer Kaydı</Form.Label>
                                    <Form.Select
                                        value={formData.vehicleData.tramer || 'YOK'}
                                        onChange={(e) => {
                                            const newTramerValue = e.target.value;
                                            handleChange('vehicleData', 'tramer', newTramerValue);

                                            // Tramer "YOK" seçildiğinde tutarı 0 yap ve açıklamayı temizle
                                            if (newTramerValue === 'YOK') {
                                                setFormattedTramerAmount('0');
                                                setFormData(prev => ({
                                                    ...prev,
                                                    vehicleData: {
                                                        ...prev.vehicleData,
                                                        tramerAmount: 0,
                                                        tramerDescription: ''
                                                    }
                                                }));
                                            }
                                        }}
                                    >
                                        <option value="YOK">Yok</option>
                                        <option value="VAR">Var</option>
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Tramer Tutarı (TL)</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">₺</span>
                                        <Form.Control
                                            type="text"
                                            value={formattedTramerAmount}
                                            onChange={(e) => handleFormattedNumberChange('tramerAmount', e.target.value)}
                                            placeholder="0"
                                            readOnly={formData.vehicleData.tramer === 'YOK'}
                                            className={formData.vehicleData.tramer === 'YOK' ? 'bg-light' : ''}
                                            required={formData.vehicleData.tramer === 'VAR'}
                                        />
                                    </div>
                                    {formData.vehicleData.tramer === 'YOK' && (
                                        <Form.Text className="text-muted">
                                            Tramer kaydı olmadığı için tutar 0 TL'dir.
                                        </Form.Text>
                                    )}
                                    {formData.vehicleData.tramer === 'VAR' && (
                                        <Form.Text className="text-muted">
                                            Tramer tutarını giriniz.
                                        </Form.Text>
                                    )}
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Tramer Açıklaması
                                        {formData.vehicleData.tramer === 'VAR' && <span className="text-danger"> *</span>}
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={formData.vehicleData.tramerDescription}
                                        onChange={(e) => handleChange('vehicleData', 'tramerDescription', e.target.value)}
                                        placeholder={
                                            formData.vehicleData.tramer === 'VAR'
                                                ? "Aracın tramer durumu hakkında açıklama yazınız..."
                                                : ""
                                        }
                                        readOnly={formData.vehicleData.tramer === 'YOK'}
                                        className={formData.vehicleData.tramer === 'YOK' ? 'bg-light' : ''}
                                        required={formData.vehicleData.tramer === 'VAR'}
                                    />
                                    {formData.vehicleData.tramer === 'YOK' ? (
                                        <Form.Text className="text-muted fst-italic small">
                                            Tramer kaydı olmadığı için açıklamaya gerek yok
                                        </Form.Text>
                                    ) : (
                                        <Form.Text className="text-muted">
                                            Tramer bilgileri giriniz. (Zorunlu alan)
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Finansman Detayları */}
                    <Col md={4}>
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">Finansman Detayları</h5>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Peşinat (TL) *</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">₺</span>
                                        <Form.Control
                                            type="text"
                                            value={formattedDownPayment}
                                            onChange={(e) => handleFormattedNumberChange('downPayment', e.target.value)}
                                            placeholder="0"
                                            required
                                        />
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Vade (Ay) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        value={formData.financialData.term}
                                        onChange={(e) => handleChange('financialData', 'term', parseInt(e.target.value) || 1)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Aylık Faiz Oranı (%) *</Form.Label>
                                    <div className="input-group">
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formData.financialData.interestRate}
                                            onChange={(e) => handleChange('financialData', 'interestRate', parseFloat(e.target.value) || 0)}
                                            required
                                        />
                                        <span className="input-group-text">%</span>
                                    </div>
                                </Form.Group>

                                {calculations && (
                                    <Card bg="light" className="mt-4">
                                        <Card.Body>
                                            <h6 className="text-primary mb-3">
                                                <FaCalculator className="me-2" />
                                                Hesaplama Sonuçları (PMT)
                                            </h6>
                                            <div>
                                                <small className="text-muted">Kredi Tutarı:</small>
                                                <p className="mb-2 fw-bold">{formatNumber(calculations.loanAmount)} TL</p>
                                            </div>
                                            <div>
                                                <small className="text-muted">Aylık Taksit:</small>
                                                <p className="mb-2 fw-bold">{formatNumber(calculations.monthlyPayment)} TL</p>
                                            </div>
                                            <div>
                                                <small className="text-muted">Toplam Ödeme:</small>
                                                <p className="mb-2 fw-bold">{formatNumber(calculations.totalPayment)} TL</p>
                                            </div>
                                            <div>
                                                <small className="text-muted">Toplam Faiz:</small>
                                                <p className="mb-0 fw-bold">{formatNumber(calculations.totalInterest)} TL</p>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    {/* Notlar */}
                    <Col md={12}>
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">Notlar</h5>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Rapor ile ilgili notlarınız..."
                                        value={formData.notes}
                                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col className="text-center">
                        <Button
                            variant="primary"
                            type="submit"
                            size="lg"
                            disabled={loading}
                            className="px-5"
                        >
                            {loading ? 'Kaydediliyor...' : (
                                <>
                                    <FaSave className="me-2" />
                                    Raporu Kaydet
                                </>
                            )}
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Container>
    );
};

export default ReportGeneratorPage;